import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { planAPI } from '../services/api';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingPrice, setEditingPrice] = useState(null);
  const [priceForm, setPriceForm] = useState({ price_usd: '', discount_percentage: '', stripe_price_id: '' });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await planAPI.getAll();
      if (response.data.success) {
        setPlans(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const billingPeriodLabels = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly'
  };

  const handleEditPrice = (planId, price) => {
    setEditingPrice({ planId, billing_period: price.billing_period });
    setPriceForm({
      price_usd: price.price_usd,
      discount_percentage: price.discount_percentage || 0,
      stripe_price_id: price.stripe_price_id || ''
    });
  };

  const handleSavePrice = async () => {
    if (!editingPrice) return;

    try {
      setError('');
      setSuccessMessage('');

      const response = await planAPI.updatePrice(
        editingPrice.planId,
        editingPrice.billing_period,
        priceForm
      );

      if (response.data.success) {
        setSuccessMessage('Price updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setEditingPrice(null);
        setPriceForm({ price_usd: '', discount_percentage: '', stripe_price_id: '' });
        fetchPlans();
      }
    } catch (err) {
      console.error('Failed to update price:', err);
      setError(err.response?.data?.error?.message || 'Failed to update price');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleCancelEdit = () => {
    setEditingPrice(null);
    setPriceForm({ price_usd: '', discount_percentage: '', stripe_price_id: '' });
  };

  const getStripePriceIdStatus = (priceId) => {
    if (!priceId) {
      return { color: 'text-red-600', icon: '❌', text: 'Not configured' };
    }
    if (priceId.startsWith('price_')) {
      return { color: 'text-green-600', icon: '✅', text: 'Configured' };
    }
    return { color: 'text-yellow-600', icon: '⚠️', text: 'Invalid format' };
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Subscription Plans" />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading plans...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Subscription Plans" />
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          )}

          <div className="mb-6">
            <p className="text-gray-600 text-sm">
              Manage subscription plans, pricing, and Stripe Price IDs. Click on any price to edit.
            </p>
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>💡 Tip:</strong> Get Stripe Price IDs from{' '}
                <a
                  href="https://dashboard.stripe.com/test/products"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold hover:text-blue-900"
                >
                  Stripe Dashboard → Products
                </a>
                {' '}(make sure you're in Test Mode)
              </p>
            </div>
          </div>

          {plans.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500">No subscription plans found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <div key={plan.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>

                  <div className="mb-4 space-y-2">
                    <p className="text-sm text-gray-600">
                      Monthly Generations: <span className="font-semibold">{plan.monthly_generation_limit}</span>
                    </p>
                    <p className="text-sm text-gray-600">
                      Advanced Models: <span className="font-semibold">{plan.supports_advanced_models ? 'Yes' : 'No'}</span>
                    </p>
                  </div>

                  {/* Pricing Options */}
                  <div className="mb-6 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Pricing Options</h4>

                    {plan.prices && plan.prices.length > 0 ? (
                      plan.prices.map((price) => {
                        const stripeStatus = getStripePriceIdStatus(price.stripe_price_id);
                        return (
                          <div key={price.id} className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-700">
                                  {billingPeriodLabels[price.billing_period] || price.billing_period}
                                </span>
                                {price.discount_percentage > 0 && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                    {price.discount_percentage}% off
                                  </span>
                                )}
                              </div>
                              <span className={`text-xs ${stripeStatus.color} font-medium`}>
                                {stripeStatus.icon} Stripe
                              </span>
                            </div>

                            {editingPrice?.planId === plan.id &&
                             editingPrice?.billing_period === price.billing_period ? (
                              <div className="space-y-3">
                                <div>
                                  <label className="text-xs font-medium text-gray-700 block mb-1">Price (USD)</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={priceForm.price_usd}
                                    onChange={(e) => setPriceForm({ ...priceForm, price_usd: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="19.00"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-gray-700 block mb-1">Discount (%)</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={priceForm.discount_percentage}
                                    onChange={(e) => setPriceForm({ ...priceForm, discount_percentage: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="0"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-gray-700 block mb-1">
                                    Stripe Price ID
                                    <span className="ml-1 text-gray-500 font-normal">(price_...)</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={priceForm.stripe_price_id}
                                    onChange={(e) => setPriceForm({ ...priceForm, stripe_price_id: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="price_1xxxxxxxxxxxxx"
                                  />
                                  <p className="mt-1 text-xs text-gray-500">
                                    From Stripe Dashboard → Products → Click price → Copy ID
                                  </p>
                                </div>
                                <div className="flex space-x-2 pt-2">
                                  <button
                                    onClick={handleSavePrice}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                className="cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded-lg transition-colors"
                                onClick={() => handleEditPrice(plan.id, price)}
                              >
                                <div className="text-2xl font-bold text-gray-900">
                                  ${parseFloat(price.price_usd).toFixed(2)}
                                </div>
                                <div className="mt-2 text-xs text-gray-600">
                                  {price.stripe_price_id ? (
                                    <div className="font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                                      {price.stripe_price_id}
                                    </div>
                                  ) : (
                                    <span className="text-red-600 font-medium">
                                      ⚠️ Stripe Price ID not set
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-blue-600 mt-2 font-medium">
                                  Click to edit →
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500">No pricing options available</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Plans;
